import React, { useCallback, useEffect, useState } from "react";
import { SSH_User } from "@danishagro/sharedShop/src/interfaces/user.interface";
import { useRolesApi } from "@danishagro/sharedShop/src/hooks/api/useRolesApi.hook";
import { useUserRolesApi } from "@danishagro/sharedShop/src/hooks/api/useUserRolesApi.hook";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { SSH_Role } from "@danishagro/sharedShop/src/interfaces/role.interface";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { SSH_Scope } from "@danishagro/sharedShop/src/interfaces/scope.interface";
import { SSH_ScopeGroup } from "@danishagro/sharedShop/src/interfaces/scopeGroup.interface";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import Skeleton from "react-loading-skeleton";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { useAccessControl, Data } from "../../hooks/useAccessControl.hook";
import { _UserItem } from "./components/UserItem/UserItem.component";
import { _InviteUserBottomSheet } from "./components/BottomSheets/InviteUserBottomSheet/InviteUserBottomSheet.component";
import { _InviteTypeBottomSheet } from "./components/BottomSheets/InviteTypeBottomSheet/InviteTypeBottomSheet.component";
import S from "./ListOfEmployeesMobile.module.scss";

export interface Props {
    invite?: boolean;
}

export const _ListOfEmployeesMobile = (_props: Props) => {
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [roles, setRoles] = useState<SSH_Role[]>([]);
    const [scopes, setScopes] = useState<SSH_Scope[]>([]);
    const [scopeGroups, setScopeGroups] = useState<SSH_ScopeGroup[]>([]);
    const [users, setUsers] = useState<SSH_User[]>([]);
    const { getRoles, getScopes, getScopeGroups } = useRolesApi();
    const { getUsersRaw, deleteUser, getDeletedUsers } = useUserRolesApi();
    const [allowInvite, setAllowInvite] = useState<boolean>(false);
    const { customerNumber, cvrNumber } = useAppData();
    const { getDictionaryString, getDictionaryItem } = useTranslations();
    const { presentBottomSheet, dismissBottomSheet } = useBottomSheet();
    const [isLoadingDeletedUsers, setIsLoadingDeletedUsers] = useState(true);
    const [deletedUsers, setDeletedUsers] = useState<SSH_User[]>([]);

    // Check user if Owner, isAdmin or isMasterAdmin
    const data: Data = globalThis?.cvrAndCustomerNumbers;
    // Grant access
    const _hasAccess = useAccessControl(data);

    // Tabs
    const [activeTab, setActiveTab] = useState("Users");

    const tabs = [
        { id: "Users", title: getDictionaryString("ActiveUsers") },
        // ...(hasAccess ? [{ id: "DeletedUsers", title: getDictionaryString("DeletedUsers") }] : []),
    ];
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const openSuccessDeleteUserBottomSheet = useCallback(
        (item: SSH_User) => {
            const userName = () => {
                return item.firstName || item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : getDictionaryString("unnamed");
            };
            presentBottomSheet(
                <div className={S.successDeleteUserWrapper}>
                    <div className={S.successDeleteUserIconWrapper}>
                        <DA_Icon
                            name={DA_IconNames.Checkmark}
                            className={S.successDeleteUserIcon}
                        />
                    </div>
                    <DA_Text alignCenter bold>
                        {getDictionaryItem("successDeleteUserTitle", { name: userName() })}
                    </DA_Text>
                    <DA_Button onClick={dismissBottomSheet} title={getDictionaryString("close")} />
                </div>,
                {
                    id: "successfully-delete-user-bottom-sheet",
                    headerTitle: getDictionaryItem("userDeleted"),
                    contentPadding: true,
                    snapPoints: ({ minHeight }) => [minHeight],
                }
            );
        },
        [dismissBottomSheet, getDictionaryItem, getDictionaryString, presentBottomSheet]
    );

    // Fetch deleted users
    const fetchDeletedUsers = useCallback(() => {
        getDeletedUsers(customerNumber)
            .then((response) => {
                setIsLoadingDeletedUsers(true);
                setDeletedUsers(Array.isArray(response) ? response : []);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setIsLoadingDeletedUsers(false);
            });
    }, [getDeletedUsers, customerNumber]);

    const fetchUsers = useCallback(() => {
        setShowSkeleton(true);
        getUsersRaw(customerNumber)
            .then((res) => {
                setUsers(Array.isArray(res.users) ? res.users : []);
                setAllowInvite(!!res.allowInvite);
            })
            .catch((error) => {
                setUsers([]);
                setAllowInvite(false);
                console.log(error);
            })
            .finally(() => setShowSkeleton(false));
    }, [getUsersRaw, customerNumber]);

    // Delete-user function
    const removeUser = useCallback(
        (id: string, item: SSH_User) => {
            deleteUser(id, customerNumber)
                .then(() => {
                    const newUsers = [...users].filter((item) => item.userId !== id);

                    setUsers(newUsers);
                    openSuccessDeleteUserBottomSheet(item);
                })
                .catch((error) => console.log(error));
        },
        [deleteUser, customerNumber, users, openSuccessDeleteUserBottomSheet]
    );

    const openInviteBottomSheet = useCallback(
        (alias: string) => {
            presentBottomSheet(
                <_InviteUserBottomSheet cvr={cvrNumber} onSubmit={fetchUsers} alias={alias} />,
                {
                    id: "invite-bottom-sheet",
                    headerTitle: getDictionaryItem("inviteType", {
                        type: getDictionaryString(alias),
                    }),
                    contentPadding: true,
                    snapPoints: ({ maxHeight }) => [maxHeight],
                }
            );
        },
        [cvrNumber, fetchUsers, getDictionaryItem, getDictionaryString, presentBottomSheet]
    );

    const openPreInviteBottomSheet = useCallback(
        (alias: string) => {
            presentBottomSheet(
                <_InviteTypeBottomSheet
                    role={alias}
                    scopes={scopes}
                    scopeGroups={scopeGroups}
                    roles={roles}
                    openInvite={() => openInviteBottomSheet(alias)}
                />,
                {
                    id: "invite-pre-bottom-sheet",
                    headerTitle: getDictionaryItem("permissionType", {
                        role: getDictionaryString(alias),
                        uppercaseFirst: true,
                    }),
                    contentPadding: true,
                    snapPoints: ({ maxHeight }) => [maxHeight],
                }
            );
        },
        [
            getDictionaryItem,
            getDictionaryString,
            openInviteBottomSheet,
            presentBottomSheet,
            roles,
            scopeGroups,
            scopes,
        ]
    );

    const openInfoBottomSheet = useCallback(
        (title: string, content: React.ReactNode, id: string) => {
            presentBottomSheet(<>{content}</>, {
                id: id,
                headerTitle: title,
                contentPadding: true,
                snapPoints: ({ minHeight }) => [minHeight],
            });
        },
        [presentBottomSheet]
    );

    useEffect(() => {
        fetchUsers();
        fetchDeletedUsers();
        Promise.all([getRoles(), getScopes(), getScopeGroups()]).then(
            ([receivedRoles, receivedScopes, receivedScopeGroups]) => {
                setRoles(receivedRoles);
                setScopes(receivedScopes);
                setScopeGroups(receivedScopeGroups);
            }
        );
    }, [getRoles, getScopes, getScopeGroups, fetchUsers, fetchDeletedUsers]);

    const handleKeyPress = (event, tabId) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault(); // Prevent default if it's Space to stop page scrolling
            handleTabChange(tabId);
        }
    };

    // Function to generate skeleton loaders
    const renderSkeletonLoaders = () => (
        <div className={S.skeletonWrapper}>
            {Array.from({ length: 10 }).map((_, index) => (
                <div key={index} className={S.userItem}>
                    <div className={S.roletype}>
                        <Skeleton width={30} height={10} />
                    </div>
                    <div className={S.name}>
                        <Skeleton width={80} />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <DA_Container>
            {allowInvite && (
                <>
                    {/* Inviter brugertyper - USER ITEM TYPE */}
                    <div className={S.titleWithIcon}>
                        {getDictionaryItem("rolesAndScopesMobileTitle")}
                        <span
                            role="presentation"
                            onClick={() =>
                                openInfoBottomSheet(
                                    getDictionaryString("rolesAndScopesModalTitle"),
                                    <div className={S.infoBottomSheetContentWrapper}>
                                        {getDictionaryItem("rolesAndScopesModalText")}
                                    </div>,
                                    "user-item-type-info"
                                )
                            }
                        >
                            <DA_Icon name={DA_IconNames.Info} className={S.icon} />
                        </span>
                    </div>

                    <div className={cn(S.list, S.listWithUserItems)}>
                        {roles.map((item) => (
                            <button
                                key={item.alias}
                                className={S.userTypeItem}
                                onClick={() => openPreInviteBottomSheet(item.alias)}
                            >
                                {getDictionaryString(item.alias, {
                                    uppercaseFirst: true,
                                })}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* Oversigt over brugere - USER ITEM */}
            <div className={S.titleWithIcon}>
                {getDictionaryItem("listOfEmployeesTitle")}
                <span
                    role="presentation"
                    onClick={() =>
                        openInfoBottomSheet(
                            getDictionaryString("listOfEmployeesTitle"),
                            <div className={S.infoBottomSheetContentWrapper}>
                                {getDictionaryItem("listOfEmployeesDescription")}
                            </div>,
                            "user-item-info"
                        )
                    }
                >
                    <DA_Icon name={DA_IconNames.Info} className={S.icon} />
                </span>
            </div>

            <div className={S.list}>
                <div className={S.tabs}>
                    {/* Render tabs */}
                    <div className={S.tabsList}>
                        {tabs.map((tab) => (
                            <div
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                onKeyDown={(event) => handleKeyPress(event, tab.id)}
                                role="button"
                                tabIndex={0}
                                className={cn(
                                    S.tabButton,
                                    activeTab === tab.id && S.activeTabButton
                                )}
                            >
                                {tab.title}
                            </div>
                        ))}
                        <div className={S.line} />
                    </div>

                    {/* Conditional rendering of content based on the active tab */}
                    {activeTab === "Users" && (
                        <div className={S.tabContent}>
                            {showSkeleton ? (
                                renderSkeletonLoaders()
                            ) : (
                                <>
                                    {users.map((user, index) => (
                                        <_UserItem
                                            {...user}
                                            onRemove={() => removeUser(user.userId, user)}
                                            onEdit={() => fetchUsers()}
                                            key={index}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === "DeletedUsers" && (
                        <div className={S.tabContent}>
                            {isLoadingDeletedUsers ? (
                                renderSkeletonLoaders()
                            ) : deletedUsers.length > 0 ? (
                                deletedUsers.map((user, index) => (
                                    <_UserItem {...user} key={index} noFooterAction />
                                ))
                            ) : (
                                <div className={S.emptyState}>
                                    <DA_EmptyState
                                        type={DA_EmptyStateTypes.NoResults}
                                        title={getDictionaryItem("noDeletedEmployeesTitle")}
                                        text={getDictionaryItem("noDeletedEmployeesText")}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* <DA_TableOfRoles
                title={getDictionaryItem("rolesAndScopesTitle")}
                titleModal={{
                    title: getDictionaryItem("rolesAndScopesModalTitle"),
                    content: getTextItem("rolesAndScopesModalText"),
                }}
                roles={roles}
                scopes={scopes}
                scopeGroups={scopeGroups}
            /> */}
        </DA_Container>
    );
};
